import type { ChatResponse, LeadData } from './types'

// Types pour les tool actions
interface ToolAction {
  type: 'collect_contact_form' | 'create_lead' | 'show_calendly' | 'end_conversation'
  data: Record<string, unknown>
  toolCallId: string
}

// Interface de réponse enrichie
interface EnrichedChatResponse extends ChatResponse {
  actions?: ToolAction[]
}

class APIClient {
  private baseUrl: string = ''
  private siteId: string = ''

  setBaseUrl(url: string) {
    this.baseUrl = url
  }

  setSiteId(id: string) {
    this.siteId = id
  }

  private getHeaders(): HeadersInit {
    return {
      'Content-Type': 'application/json',
      'x-site-id': this.siteId,
    }
  }

  private async parseError(response: Response) {
    try {
      const data = await response.json()
      return data?.error ? `${data.error}${data.details ? `\n${data.details}` : ''}` : 'Erreur API'
    } catch {
      return 'Erreur API'
    }
  }

  /**
   * Envoie un message au chatbot
   */
  async sendMessage(
    sessionId: string | null,
    message: string
  ): Promise<EnrichedChatResponse> {
    const response = await fetch(`${this.baseUrl}/api/chatbot/chat`, {
      method: 'POST',
      headers: this.getHeaders(),
      body: JSON.stringify({
        sessionId,
        message,
      }),
    })

    if (!response.ok) {
      throw new Error(await this.parseError(response))
    }

    return response.json()
  }

  /**
   * Initialise une nouvelle conversation
   */
  async initChat(): Promise<ChatResponse> {
    const response = await fetch(`${this.baseUrl}/api/chatbot/chat`, {
      method: 'POST',
      headers: this.getHeaders(),
      body: JSON.stringify({
        isInitial: true,
      }),
    })

    if (!response.ok) {
      throw new Error(await this.parseError(response))
    }

    return response.json()
  }

  /**
   * Soumet les données du formulaire de contact
   */
  async submitForm(
    sessionId: string | null,
    formData: {
      firstName: string
      lastName: string
      email: string
      phone: string
      company?: string
    }
  ): Promise<EnrichedChatResponse> {
    const response = await fetch(`${this.baseUrl}/api/chatbot/chat`, {
      method: 'POST',
      headers: this.getHeaders(),
      body: JSON.stringify({
        sessionId,
        formData,
      }),
    })

    if (!response.ok) {
      throw new Error(await this.parseError(response))
    }

    return response.json()
  }

  /**
   * Crée un lead dans la base de données
   */
  async createLead(sessionId: string, data: LeadData | Record<string, unknown>): Promise<{
    success: boolean
    referenceNumber?: string
    message?: string
  }> {
    const response = await fetch(`${this.baseUrl}/api/chatbot/lead`, {
      method: 'POST',
      headers: this.getHeaders(),
      body: JSON.stringify({
        sessionId,
        ...data,
      }),
    })

    if (!response.ok) {
      throw new Error('Failed to create lead')
    }

    return response.json()
  }

  /**
   * Récupère l'URL Calendly pré-remplie
   */
  async getCalendlyUrl(
    firstName?: string,
    lastName?: string,
    email?: string
  ): Promise<{ embedUrl: string }> {
    const params = new URLSearchParams()
    if (firstName) params.append('firstName', firstName)
    if (lastName) params.append('lastName', lastName)
    if (email) params.append('email', email)

    const response = await fetch(
      `${this.baseUrl}/api/chatbot/calendly?${params.toString()}`,
      {
        headers: this.getHeaders(),
      }
    )

    if (!response.ok) {
      throw new Error('Failed to get Calendly URL')
    }

    return response.json()
  }

  /**
   * Enregistre un rendez-vous Calendly
   */
  async registerAppointment(
    sessionId: string,
    data: {
      eventUri?: string
      inviteeUri?: string
      scheduledAt?: string
    }
  ): Promise<{ success: boolean }> {
    const response = await fetch(`${this.baseUrl}/api/chatbot/appointment`, {
      method: 'POST',
      headers: this.getHeaders(),
      body: JSON.stringify({
        sessionId,
        ...data,
      }),
    })

    if (!response.ok) {
      throw new Error('Failed to register appointment')
    }

    return response.json()
  }

  /**
   * Sauvegarde des messages dans la conversation (sans déclencher GPT)
   * Utilisé pour synchroniser les messages client-side avec la DB
   */
  async saveMessages(
    sessionId: string,
    messages: Array<{ role: 'user' | 'assistant'; content: string }>
  ): Promise<{ success: boolean }> {
    const response = await fetch(`${this.baseUrl}/api/chatbot/messages`, {
      method: 'POST',
      headers: this.getHeaders(),
      body: JSON.stringify({
        sessionId,
        messages,
      }),
    })

    if (!response.ok) {
      console.error('Failed to save messages')
      return { success: false }
    }

    return response.json()
  }
}

export const API_CLIENT = new APIClient()
